import { Progress } from "@/components/ui/progress";
import { 
  HoverCard, 
  HoverCardContent, 
  HoverCardTrigger 
} from "@/components/ui/hover-card";
import { cn } from "@/lib/utils";
import { formatNumber, formatNumberShort } from "@/lib/utils";
import { Zap, TrendingUp, AlertTriangle } from "lucide-react";
import { useCredits } from "@/hooks/useCredits";
import { useNavigate } from "react-router-dom";

interface CreditUsageBarProps {
  // Make these optional - if not provided, we'll fetch them internally
  planCredits?: number;
  purchasedCredits?: number;
  totalCredits?: number;
  usedCredits?: number;
  className?: string;
  showDetails?: boolean;
  onClick?: () => void;
}

export function CreditUsageBar({
  planCredits: externalPlanCredits,
  purchasedCredits: externalPurchasedCredits,
  totalCredits: externalTotalCredits,
  usedCredits: externalUsedCredits,
  className,
  showDetails = true,
  onClick
}: CreditUsageBarProps) {
  // Fetch credits internally if not provided as props
  const { balance, usage, loading } = useCredits();
  const navigate = useNavigate();
  
  // Use external props if provided, otherwise use fetched data
  const planCredits = externalPlanCredits ?? balance?.plan_credits ?? 0;
  const purchasedCredits = externalPurchasedCredits ?? balance?.purchased_credits ?? 0;
  const usedCredits = externalUsedCredits ?? usage?.month?.totalConsumed ?? 0;
  const totalCredits = planCredits + purchasedCredits + usedCredits;
  
  // Show loading state if we're fetching data and no external props provided
  const isLoading = loading && (
    externalPlanCredits === undefined || 
    externalPurchasedCredits === undefined || 
    externalTotalCredits === undefined || 
    externalUsedCredits === undefined
  );
  
  // Don't render if we're loading and have no data
  if (isLoading && totalCredits === 0) {
    return null;
  }

  const percentageUsed = totalCredits > 0 ? (usedCredits / totalCredits) * 100 : 0;
  const remainingCredits = Math.max(0, totalCredits - usedCredits);
  
  // Determine color based on usage percentage
  const getProgressColor = () => {
    if (percentageUsed >= 95) return "bg-red-500";
    if (percentageUsed >= 80) return "bg-yellow-500";
    return "bg-green-500";
  };

  const getTextColor = () => {
    if (percentageUsed >= 95) return "text-red-600";
    if (percentageUsed >= 80) return "text-yellow-600";
    return "text-green-600";
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      navigate('/credits');
    }
  };

  const getStatusIcon = () => {
    if (percentageUsed >= 95) return <AlertTriangle className="w-4 h-4 text-red-500" />;
    if (percentageUsed >= 80) return <TrendingUp className="w-4 h-4 text-yellow-500" />;
    return <Zap className="w-4 h-4 text-green-500" />;
  };

  const getBackgroundGradient = () => {
    if (percentageUsed >= 95) return "bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/30 border-red-200/50";
    if (percentageUsed >= 80) return "bg-gradient-to-br from-yellow-50 to-orange-100 dark:from-yellow-900/20 dark:to-orange-800/30 border-yellow-200/50";
    return "bg-gradient-to-br from-green-50 to-emerald-100 dark:from-green-900/20 dark:to-emerald-800/30 border-green-200/50";
  };

  const progressBar = (
    <div className={cn("space-y-3 p-4 rounded-lg border transition-all duration-300 hover:shadow-sm", getBackgroundGradient(), className)}>
      <div className="flex flex-col items-center text-center space-y-2">
        <div className="flex justify-center">
          {getStatusIcon()}
        </div>
        <div className="text-sm font-semibold text-gray-800 dark:text-gray-200">
          {formatNumberShort(remainingCredits)} Credits Remaining
        </div>
      </div>
      
      <div className="relative">
        <Progress 
          value={percentageUsed} 
          className="h-3 bg-white/60 dark:bg-gray-700/60 rounded-full shadow-inner"
          indicatorClassName={cn(
            getProgressColor(),
            "transition-all duration-700 ease-out rounded-full shadow-sm",
            percentageUsed >= 95 ? "animate-pulse" : ""
          )}
        />
        {percentageUsed > 0 && (
          <div 
            className="absolute top-0 left-0 h-full bg-white/30 rounded-full transition-all duration-1000 ease-out"
            style={{ width: `${Math.min(percentageUsed * 0.6, 60)}%` }}
          />
        )}
      </div>
      
      {showDetails && (
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-4">
            <span className="font-medium text-gray-600 dark:text-gray-300">
              <span className={getTextColor()}>{formatNumberShort(usedCredits)}</span> used
            </span>
          </div>
          <span className={cn("text-xs font-semibold", getTextColor())}>
            {Math.round(percentageUsed)}% used
          </span>
        </div>
      )}
    </div>
  );

  if (!showDetails) {
    return (
      <button
        onClick={handleClick}
        className="w-full text-left hover:opacity-80 transition-opacity"
      >
        {progressBar}
      </button>
    );
  }

  return (
    <HoverCard>
      <HoverCardTrigger asChild>
        <button
          onClick={handleClick}
          className="w-full text-left hover:opacity-80 transition-opacity"
        >
          {progressBar}
        </button>
      </HoverCardTrigger>
      <HoverCardContent 
        className="w-80 bg-gradient-to-br from-white to-gray-50 dark:from-gray-900 dark:to-gray-800 border shadow-lg z-[9999]"
        side="right"
        align="start"
        sideOffset={8}
        avoidCollisions={true}
        collisionPadding={16}
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {getStatusIcon()}
              <h4 className="text-sm font-bold text-gray-800 dark:text-gray-200">Credit Breakdown</h4>
            </div>
            <span className={cn("font-semibold text-sm", getTextColor())}>
              {Math.round(percentageUsed)}% used
            </span>
          </div>
          
          <div className="space-y-3 text-sm">
            {purchasedCredits > 0 && (
              <div className="flex justify-between items-center p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <span className="text-blue-700 dark:text-blue-300 font-medium">Purchased Credits:</span>
                <span className="font-bold text-blue-600 dark:text-blue-400">
                  +{formatNumber(purchasedCredits)}
                </span>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div className="p-3 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 rounded-lg text-center">
                <div className="text-xs text-muted-foreground mb-1">Total Available</div>
                <div className="font-bold text-gray-800 dark:text-gray-200">{formatNumber(totalCredits)}</div>
              </div>
              <div className="p-3 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 rounded-lg text-center">
                <div className="text-xs text-muted-foreground mb-1">Used</div>
                <div className={cn("font-bold", getTextColor())}>{formatNumber(usedCredits)}</div>
              </div>
            </div>
            <div className="p-3 bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20 rounded-lg border border-emerald-200/50">
              <div className="flex justify-between items-center">
                <span className="font-semibold text-emerald-800 dark:text-emerald-200">Remaining:</span>
                <span className={cn("font-bold text-lg", getTextColor())}>
                  {formatNumberShort(remainingCredits)}
                </span>
              </div>
            </div>
          </div>
          
          {percentageUsed >= 80 && (
            <div className={cn(
              "p-3 rounded-lg border-l-4 transition-all duration-300",
              percentageUsed >= 95 
                ? "bg-red-50 dark:bg-red-900/20 border-red-400 text-red-800 dark:text-red-200"
                : "bg-yellow-50 dark:bg-yellow-900/20 border-yellow-400 text-yellow-800 dark:text-yellow-200"
            )}>
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <p className="text-xs font-medium leading-relaxed">
                  {percentageUsed >= 95 
                    ? "You're almost out of credits. Consider purchasing more to continue creating content."
                    : "You're running low on credits. Plan your usage or purchase more to avoid interruptions."}
                </p>
              </div>
            </div>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}